import type { WorkbookDefinition } from "@erp/shared-types";

/** What a Reference can point to. Only Items can be referenced. */
export type WorkbookItemKind = "file" | "source" | "sheet" | "output";

export interface WorkbookItem {
  id: string;
  kind: WorkbookItemKind;
  /** The Item's current name. */
  name: string;
}

/** A resolved Reference. A removed Item resolves as `missing` and cannot be opened. */
export type WorkbookReference = WorkbookItem | { id: string; kind: "missing" };

export interface WorkbookReferences {
  /** Every Item in picker order: Files, Sources, Worksheets, then Outputs. */
  readonly items: readonly WorkbookItem[];
  resolve: (id: string) => WorkbookReference;
  /** Replaces every `{workbook:ID}` in `text` with `name` of its resolved Reference. */
  replace: (
    text: string,
    name: (reference: WorkbookReference) => string,
  ) => string;
}

/** `{workbook:ID}`: the `{key:id}` grammar the mention editor reads and writes. */
const REFERENCE = /\{workbook:([^\s}]+)\}/g;

/** The parts of a Definition that name References. */
export type ReferenceItems = Pick<
  WorkbookDefinition,
  "files" | "sources" | "sheets" | "outputs"
>;

export function workbookReferences(
  definition: ReferenceItems,
): WorkbookReferences {
  const items: WorkbookItem[] = [
    ...definition.files.map((item) => ({
      id: item.id,
      kind: "file" as const,
      name: item.name,
    })),
    ...definition.sources.map((item) => ({
      id: item.id,
      kind: "source" as const,
      name: item.name,
    })),
    ...definition.sheets.map((item) => ({
      id: item.id,
      kind: "sheet" as const,
      name: item.name,
    })),
    ...definition.outputs.map((item) => ({
      id: item.id,
      kind: "output" as const,
      name: item.name,
    })),
  ];
  const byId = new Map(items.map((item) => [item.id, item]));
  const resolve = (id: string): WorkbookReference =>
    byId.get(id) ?? { id, kind: "missing" };
  return {
    items,
    resolve,
    replace: (text, name) =>
      text.replace(REFERENCE, (_match, id: string) => name(resolve(id))),
  };
}

/**
 * One line of Instruction Markdown as plain text: References become names, and
 * links, list and heading markers and emphasis are dropped.
 */
export function referencePlainText(
  references: WorkbookReferences,
  markdown: string,
  name: (reference: WorkbookReference) => string,
): string {
  return references
    .replace(markdown, name)
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/^\s*(?:[-*#>]\s*|\d+\.\s*)/, "")
    .replace(/[*#`]/g, "")
    .trim();
}
